// Shared helpers for file-download endpoints (CSV/PDF reports). The server sets
// the real filename via Content-Disposition; callers fall back to a local name
// when the header is absent (e.g. not exposed via Access-Control-Expose-Headers).

export interface ReportDownload {
  blob: Blob;
  filename: string | null;
}

export function parseContentDispositionFilename(
  response: Response
): string | null {
  const header = response.headers.get("Content-Disposition");
  if (!header) return null;
  // Prefer RFC 5987 `filename*=UTF-8''...` over the plain `filename="..."`.
  const encoded = header.match(/filename\*=(?:UTF-8'')?"?([^";]+)"?/i);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      return encoded[1];
    }
  }
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1] : null;
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Longest range the report endpoints accept, both dates inclusive. */
export const REPORT_MAX_DAYS = 365;

/**
 * True when a YYYY-MM-DD range is longer than the API accepts. Both dates are
 * inclusive, so the API refuses end - start >= 365 days (SFAPI-156); guarding
 * the same way lets the caller show its own message instead of a 422.
 */
export function exceedsReportRange(
  startDate: string,
  endDate: string
): boolean {
  const days = Math.round(
    (Date.parse(endDate) - Date.parse(startDate)) / 86_400_000
  );

  return days >= REPORT_MAX_DAYS;
}
